import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Loader2, Calendar as CalendarIcon, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import MemberAutocomplete from "@/components/finance/MemberAutocomplete";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { sortGeneralFirstByName } from "@/lib/utils";

const INCOME_CATEGORIES_DEFAULT = ["Offerings", "Tithes", "Donations"];
const EXPENSE_CATEGORIES_DEFAULT = ["Utilities", "Salaries", "Maintenance", "Supplies", "Missions", "Events", "Rent/Mortgage", "Insurance", "Transportation"];
const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Mobile Money", "Card", "Cheque", "Other"];
const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";
const today = () => new Date().toISOString().split("T")[0];

interface Row {
  id: string;
  record_type: "income" | "expense";
  member_id: string | null;
  member_name: string;
  amount: string;
  category: string;
  orgPathIds: string[];
  organization: string;
  payment_method: string;
  date: string;
  notes: string;
}

type Organization = {
  id: string;
  name: string;
  type: string;
  level: string;
  parent_id: string | null;
};

type MemberOption = {
  id: string;
  full_name: string;
};

const emptyRow = (): Row => ({
  id: crypto.randomUUID(),
  record_type: "income",
  member_id: null,
  member_name: "",
  amount: "",
  category: "",
  orgPathIds: [],
  organization: "",
  payment_method: "",
  date: today(),
  notes: "",
});

function formatWithCommas(val: string): string {
  if (!val) return "";
  const num = parseFloat(val.replace(/,/g, ""));
  if (isNaN(num)) return val;
  return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function AmountInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const displayValue = formatWithCommas(value);
  return (
    <div className="relative">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
      <Input
        type="text"
        inputMode="numeric"
        placeholder="0"
        value={displayValue}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, "");
          const parts = raw.split(".");
          const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          const formatted = parts.length > 1 ? `${int}.${parts[1]}` : int;
          onChange(formatted.replace(/,/g, ""));
        }}
        className="h-9 w-full pl-6"
      />
    </div>
  );
}

interface EditRecord {
  id: string;
  member_id?: string | null;
  member_name: string | null;
  amount: number;
  date: string;
  category: string;
  payment_method: string | null;
  notes: string | null;
  record_type?: string;
}

function rowFromEditRecord(rec: EditRecord): Row {
  const notes = rec.notes || "";
  const lines = notes.split("\n");
  const orgLine = lines.find((l) => l.startsWith(MINISTRY_NOTE_PREFIX) || l.startsWith(LEGACY_ORG_NOTE_PREFIX)) || "";
  const idsLine = lines.find((l) => l.startsWith(MINISTRY_IDS_NOTE_PREFIX) || l.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)) || "";
  const organization = orgLine.replace(MINISTRY_NOTE_PREFIX, "").replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  const orgPathIds = idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((s) => s.trim())
    .filter(Boolean);
  const cleanNotes = lines
    .filter((l) =>
      !l.startsWith(MINISTRY_NOTE_PREFIX)
      && !l.startsWith(LEGACY_ORG_NOTE_PREFIX)
      && !l.startsWith(MINISTRY_IDS_NOTE_PREFIX)
      && !l.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
      && l.trim() !== "Non-member"
    )
    .join("\n")
    .trim();
  return {
    id: crypto.randomUUID(),
    record_type: ((rec.record_type || "income") as "income" | "expense"),
    member_id: rec.member_id || null,
    member_name: rec.member_name || "",
    amount: String(rec.amount),
    category: rec.category,
    orgPathIds,
    organization,
    payment_method: rec.payment_method || "",
    date: rec.date,
    notes: cleanNotes,
  };
}

export default function BulkEntryTable({ onDone, editRecord }: { onDone: () => void; editRecord?: EditRecord }) {
  const [rows, setRows] = useState<Row[]>(() => editRecord ? [rowFromEditRecord(editRecord)] : [emptyRow()]);
  const { user, role } = useAuth();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess } = usePermissions();
  const { profile } = useChurch();
  const queryClient = useQueryClient();

  const { data: budgetCategories = [] } = useQuery({
    queryKey: ["budget_categories", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("annual_budgets")
        .select("category, record_type")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return (data || []) as { category: string; record_type: string }[];
    },
    enabled: !!profile?.id,
  });

  const incomeCategories = useMemo(() => {
    const custom = budgetCategories
      .filter((b) => b.record_type === "income" && !INCOME_CATEGORIES_DEFAULT.includes(b.category))
      .map((b) => b.category);
    return [...INCOME_CATEGORIES_DEFAULT, ...new Set(custom)];
  }, [budgetCategories]);

  const expenseCategories = useMemo(() => {
    const custom = budgetCategories
      .filter((b) => b.record_type === "expense" && !EXPENSE_CATEGORIES_DEFAULT.includes(b.category))
      .map((b) => b.category);
    return [...EXPENSE_CATEGORIES_DEFAULT, ...new Set(custom)];
  }, [budgetCategories]);

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, type, level, parent_id")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as Organization[]);
    },
    enabled: !!profile?.id,
  });

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ["members_list_full", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name")
        .eq("church_id", profile!.id)
        .order("full_name");
      if (error) throw error;
      return (data || []) as MemberOption[];
    },
    enabled: !!profile?.id,
  });

  const financeScoped = role !== "admin" && hasFinanceScopedAccess();
  const allowedFinanceOrgIds = getFinanceOrganizationIds();

  const accessibleOrgSet = useMemo(() => {
    if (!financeScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedFinanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [financeScoped, allowedFinanceOrgIds, organizations]);

  const accessibleOrganizations = useMemo(() => {
    if (!financeScoped || !accessibleOrgSet) return organizations;
    return organizations.filter((org) => accessibleOrgSet.has(org.id));
  }, [organizations, financeScoped, accessibleOrgSet]);

  const selectableMinistries = useMemo(
    () => sortGeneralFirstByName(accessibleOrganizations.filter((org) => org.type === "ministry" || !org.type)),
    [accessibleOrganizations]
  );

  // When editing older records, collapse any path to one selected ministry id.
  useEffect(() => {
    if (!editRecord || organizations.length === 0) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.orgPathIds.length === 0) return r;
        const selectedId = [...r.orgPathIds]
          .reverse()
          .find((id) => selectableMinistries.some((org) => org.id === id));
        if (!selectedId) return { ...r, orgPathIds: [], organization: "" };
        const selectedOrg = selectableMinistries.find((org) => org.id === selectedId);
        return {
          ...r,
          orgPathIds: [selectedId],
          organization: selectedOrg?.name || r.organization,
        };
      })
    );
  }, [editRecord, organizations, selectableMinistries]);

  const updateRow = <K extends keyof Row>(id: string, key: K, value: Row[K]) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (id: string) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const isRowFilled = (r: Row) => !!(r.amount || r.category || r.organization || r.payment_method || r.notes);
  const handleDeleteClick = (row: Row) => {
    if (isRowFilled(row)) {
      setConfirmDeleteId(row.id);
    } else {
      removeRow(row.id);
    }
  };
  const handleConfirmDelete = () => {
    if (confirmDeleteId) removeRow(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const createMemberMutation = useMutation({
    mutationFn: async (fullName: string) => {
      const trimmed = fullName.trim();
      if (!trimmed) throw new Error("Member name is required");
      if (!profile?.id) throw new Error("No church profile found");

      const nameParts = trimmed.split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || null;
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : null;

      const { data, error } = await supabase
        .from("members")
        .insert({
          full_name: trimmed,
          first_name: firstName,
          last_name: lastName,
          church_id: profile.id,
        })
        .select("id, full_name")
        .single();

      if (error) throw error;
      return data as MemberOption;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members_list_full"] });
      queryClient.invalidateQueries({ queryKey: ["members", "finance_member_lookup"] });
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  const mutation = useMutation({
    mutationFn: async (validRows: Row[]) => {
      const buildNotes = (r: Row) => [
        r.organization ? `${MINISTRY_NOTE_PREFIX}${r.organization}` : "",
        r.orgPathIds.length > 0 ? `${MINISTRY_IDS_NOTE_PREFIX}${r.orgPathIds.join(" > ")}` : "",
        r.notes.trim() || "",
      ].filter(Boolean).join("\n") || null;

      if (editRecord) {
        const r = validRows[0];
        const { error } = await supabase.from("finance_records").update({
          notes: buildNotes(r),
          member_id: r.member_id,
          member_name: r.member_name.trim() || null,
          amount: parseFloat(r.amount),
          category: r.category,
          record_type: r.record_type,
          payment_method: r.payment_method || null,
          date: r.date,
        }).eq("id", editRecord.id);
        if (error) throw error;
      } else {
        const payload = validRows.map((r) => ({
          notes: buildNotes(r),
          member_id: r.member_id,
          member_name: r.member_name.trim() || null,
          amount: parseFloat(r.amount),
          category: r.category,
          record_type: r.record_type,
          payment_method: r.payment_method || null,
          date: r.date,
          created_by: user?.id || null,
          church_id: profile?.id,
        }));
        const { error } = await supabase.from("finance_records").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["finance_records"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });
      if (editRecord) {
        
        onDone();
      } else {
        
        setRows([emptyRow()]);
        window.setTimeout(() => {
          onDone();
        }, 900);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSaveAll = () => {
    if (!profile?.id) {
      toast.error("Church profile not found. Please refresh and try again.");
      return;
    }
    const valid = rows.filter(
      (r) =>
        r.amount
        && r.category
        && parseFloat(r.amount) > 0
        && !!r.date
        && r.orgPathIds.length > 0
        && !!r.payment_method
    );
    if (valid.length === 0) {
      toast.error("Fill in at least one row with amount, category, date, ministry, and payment");
      return;
    }
    if (rows.some((r) => r.amount && r.category && parseFloat(r.amount) > 0 && r.orgPathIds.length === 0)) {
      toast.error("Select a ministry for each filled entry");
      return;
    }
    if (rows.some((r) => r.amount && r.category && parseFloat(r.amount) > 0 && !r.payment_method)) {
      toast.error("Select a payment method for each filled entry");
      return;
    }
    if (rows.some((r) => r.amount && r.category && parseFloat(r.amount) > 0 && !r.date)) {
      toast.error("Select a date for each filled entry");
      return;
    }
    if (financeScoped && valid.some((r) => r.orgPathIds.length === 0)) {
      toast.error("Select an allowed ministry for each entry");
      return;
    }
    mutation.mutate(valid);
  };

  const filledCount = rows.filter(
    (r) =>
      r.amount
      && r.category
      && parseFloat(r.amount) > 0
      && !!r.date
      && r.orgPathIds.length > 0
      && !!r.payment_method
  ).length;

  return (
    <div className="stat-card rounded-none p-0 overflow-visible relative !transform-none hover:!translate-y-0 hover:!border-border/60">
      {/* Always-visible X close button */}
      <button type="button" onClick={onDone} className="absolute top-2 right-2 z-10 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"><XIcon className="w-4 h-4" /></button>

      {/* ── Entry form ── */}
      <>
        {rows.map((row, idx) => (
          <div key={row.id} className="px-4 py-3 border-b border-border/30 last:border-b-0">
            {/* Mobile: stacked layout; Desktop: single row */}
            <div className={`grid grid-cols-2 gap-2 items-end sm:flex-none ${row.category === "Tithes" || row.category === "Donations" ? "sm:grid-cols-[100px_110px_1fr_1fr_1fr_1fr_1fr_auto]" : "sm:grid-cols-[100px_110px_1fr_1fr_1fr_1fr_auto]"}`}>
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Type *</p>
                <Select value={row.record_type} onValueChange={(v) => { updateRow(row.id, "record_type", v as "income" | "expense"); updateRow(row.id, "category", ""); }}>
                  <SelectTrigger className="h-9 w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Amount *</p>
                <AmountInput value={row.amount} onChange={(v) => updateRow(row.id, "amount", v)} />
              </div>
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Category *</p>
                <Select value={row.category} onValueChange={(v) => updateRow(row.id, "category", v)}>
                  <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{(row.record_type === "expense" ? expenseCategories : incomeCategories).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(row.category === "Tithes" || row.category === "Donations") && (
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Source</p>
                <MemberAutocomplete
                  value={row.member_name}
                  onChange={(value) => {
                    updateRow(row.id, "member_name", value);
                    const match = members.find(
                      (m) => m.full_name.trim().toLowerCase() === value.trim().toLowerCase()
                    );
                    updateRow(row.id, "member_id", match ? match.id : null);
                  }}
                  onMemberSelect={(member) => {
                    if (!member) return;
                    updateRow(row.id, "member_name", member.full_name);
                    updateRow(row.id, "member_id", member.id);
                  }}
                  onCreateMember={async (name) => {
                    const trimmed = name.trim();
                    if (!trimmed) return null;
                    const existing = members.find(
                      (member) => member.full_name.trim().toLowerCase() === trimmed.toLowerCase()
                    );
                    if (existing) return existing;
                    const created = await createMemberMutation.mutateAsync(trimmed);
                    
                    return created;
                  }}
                  options={members}
                  loading={membersLoading}
                  creating={createMemberMutation.isPending}
                  placeholder="Source name"
                  className="h-9 w-full"
                />
              </div>
              )}
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Date *</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-9 w-full justify-start text-left font-normal">
                      <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                      {row.date ? format(parseISO(row.date), "MM/dd/yyyy") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={row.date ? parseISO(row.date) : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        updateRow(row.id, "date", format(d, "yyyy-MM-dd"));
                      }}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Ministry *</p>
                <Select
                  value={row.orgPathIds[0] || ""}
                  onValueChange={(v) => {
                    const selectedOrg = selectableMinistries.find((org) => org.id === v);
                    updateRow(row.id, "orgPathIds", [v]);
                    updateRow(row.id, "organization", selectedOrg?.name || "");
                  }}
                >
                  <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Select ministry" /></SelectTrigger>
                  <SelectContent>
                    {selectableMinistries.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Payment *</p>
                <Select value={row.payment_method} onValueChange={(v) => updateRow(row.id, "payment_method", v)}>
                  <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Method" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1 pb-0.5">
                {confirmDeleteId === row.id ? (
                  <div className="flex items-center gap-0.5">
                    <button type="button" onClick={handleConfirmDelete} className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors" title="Confirm delete">
                      <Check className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setConfirmDeleteId(null)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors" title="Cancel">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => handleDeleteClick(row)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 px-3 py-3 border-t border-border/40">
        {editRecord ? <div /> : <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto min-h-[44px] sm:min-h-0" onClick={addRow}><Plus className="w-4 h-4 mr-1" /> Add Row</Button>}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          {!editRecord && <span className="text-sm text-muted-foreground text-center sm:text-left">{filledCount} entries ready</span>}
          <Button size="sm" className="w-full sm:w-auto min-h-[44px] sm:min-h-0" onClick={handleSaveAll} disabled={mutation.isPending || filledCount === 0}>
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> {editRecord ? "Save Changes" : "Save All Entries"}</>}
          </Button>
        </div>
      </div>
      </>
    </div>
  );
}
